import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { useCallback, useEffect, useMemo, useState } from 'react';

function getPreferredConnector(connectors: ReturnType<typeof useConnect>['connectors']) {
  return (
    connectors.find((connector) => connector.id.includes('baseAccount')) ||
    connectors.find((connector) => connector.id.includes('coinbase')) ||
    connectors.find((connector) => connector.id.includes('metaMask')) ||
    connectors.find((connector) => connector.id.includes('injected')) ||
    connectors[0]
  );
}

export function useWebAppAuth() {
  const [ready, setReady] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const { address, isConnected, isConnecting, isReconnecting, status } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    setReady(true);
    setIsAppReady(true);
  }, []);

  const authenticated = isConnected && !!address;

  const user = useMemo(() => {
    if (!authenticated || !address) return null;

    return {
      id: address,
      address,
      wallet: { address },
    };
  }, [address, authenticated]);

  const login = useCallback(async () => {
    if (authenticated) return;

    const connector = getPreferredConnector(connectors);
    if (!connector) {
      console.error('No wallet connector available');
      return;
    }

    try {
      await connectAsync({ connector });
    } catch (error) {
      console.error('Login error:', error);
    }
  }, [authenticated, connectAsync, connectors]);

  const logout = useCallback(async () => {
    try {
      disconnect();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [disconnect]);

  const getAccessToken = useCallback(async () => null, []);

  const setAppReady = useCallback(() => {
    setIsAppReady(true);
  }, []);

  const callAppReady = useCallback(async () => {
    setIsAppReady(true);
    return true;
  }, []);

  return {
    ready,
    authenticated,
    user,
    login,
    logout,
    getAccessToken,
    isAppReady,
    setAppReady,
    callAppReady,
    address,
    isConnected,
    isConnecting,
    isReconnecting,
    status,
  };
}
